import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import React, { FunctionComponent } from 'react';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
  },
  content: {
    '& h1': {
      fontSize: '40px',
      lineHeight: '45px',
      fontFamily: 'Cera Round Pro',
      fontWeight: 'normal',
      margin: theme.spacing(4, 0, 2),
      textAlign: 'center',
    },
    '& h2': {
      fontSize: '28px',
      lineHeight: '34px',
      fontFamily: 'Cera Round Pro',
      fontWeight: 'normal',
      margin: theme.spacing(5, 0, 2),
    },
    '& p': {
      fontSize: '20px',
      lineHeight: '26px',
      fontFamily: 'Cera Round Pro',
      margin: theme.spacing(0, 0, 2),
    },
    '& ul': {
      fontSize: '20px',
      lineHeight: '26px',
      fontFamily: 'Cera Round Pro',
      margin: theme.spacing(0, 0, 2),
      paddingLeft: theme.spacing(3),
    },
    '& li': {
      marginBottom: theme.spacing(1),
    },
    '& strong': {
      fontWeight: 600,
    },
  },
  date: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
}));

/**
 * Privacy statement for the Apteekkihälytin / Pharmacy notifier app.
 * Linked from the app and store listings only — not from the homepage menu or sitemap.
 */
const ApteekkihalytinTietosuoja: FunctionComponent = () => {
  const classes = useStyles();

  return (
    <Container className={classes.container} maxWidth="md">
      <Typography className={classes.content} component="article">
        <h1>Apteekkihälyttimen tietosuojaseloste</h1>
        <p className={classes.date}>28.9.2026</p>

        <h2>Rekisterinpitäjä ja käsittelijä</h2>
        <p>
          <strong>Palveluntarjoaja (henkilötietojen käsittelijä):</strong>
          <br />
          ApoDigi Oy (2830326-5)
          <br />
          Kasarmikatu 4 A 8
          <br />
          70110 Kuopio
          <br />
          Yhteyshenkilö: tietosuojavastaava@treet.fi
        </p>
        <p>
          <strong>Rekisterinpitäjä:</strong>
          <br />
          Laitetta käyttävä apteekki toimii rekisterinpitäjänä sovellukseen syötettävien henkilötietojen (kuten laitteen
          nimeen syötetyn työntekijän nimen) osalta.
        </p>

        <h2>Mikä sovellus on</h2>
        <p>
          Apteekkihälytin on sovellus, joka on suunniteltu apteekin yhteiskäyttöpuhelimeen. Apteekeissa on usein yksi
          tai useampi yhteisessä käytössä oleva puhelin, jolla seurataan saapuvia tehtäviä. Sovellus ilmoittaa apteekin
          henkilökunnalle käsiteltävästä työstä, kuten uudesta tilauksesta, chat-pyynnöstä tai soittopyynnöstä.
        </p>
        <p>
          Sovelluksessa ei ole henkilökohtaisia käyttäjätilejä. Apteekki parittaa puhelimen omasta
          Treet-hallintajärjestelmästään.
        </p>
        <p>
          Tämä seloste koskee vain Apteekkihälytin-sovellusta. Treet-asiointisovelluksen asiakasrekisteristä on
          erillinen tietosuojaseloste.
        </p>

        <h2>Mitä tietoja käsitellään</h2>
        <p>
          <strong>ApoDigi Oy:n palvelimelle tallennetaan:</strong>
        </p>
        <ul>
          <li>
            Laitteen nimi, jonka apteekki vahvistaa parituksen yhteydessä (laitteen mallinimi tai apteekin syöttämä
            nimi, joka voi sisältää työntekijän nimen)
          </li>
          <li>Apteekki, johon laite on paritettu</li>
          <li>Ilmoitusten lähetystunniste (Firebase Cloud Messaging -tunniste)</li>
          <li>Hälytysasetukset (mitä hälytyksiä laite soittaa ja mihin aikaan)</li>
          <li>Parituksen ja viimeisimmän käytön ajankohdat sekä tieto laitteen tilasta (aktiivinen / poistettu)</li>
        </ul>
        <p>
          <strong>Laitteelle paikallisesti tallennettavat tiedot:</strong>
          <br />
          Puhelimeen tallennetaan laitteen tunniste, paritusavain, apteekin tunniste, palvelimen osoite ja kielivalinta.
          Kielivalintaa ei lähetetä palvelimelle. Muita tietoja käytetään vain pyyntöjen tunnistamiseen apteekin
          Treet-palvelussa. Laitteelle tallennettavia tietoja käytetään ainoastaan sovelluksen toiminnallisuuden
          ylläpitämiseen, eikä sovelluksessa käytetä kolmannen osapuolen evästeitä tai seurantateknologioita.
        </p>
        <p>
          <strong>Laitteen käyttöoikeudet:</strong>
        </p>
        <ul>
          <li>
            <strong>Kamera:</strong> Käytetään vain parituksen QR-koodin lukemiseen. Kuvaa ei tallenneta eikä lähetetä
            laitteelta.
          </li>
          <li>
            <strong>Ilmoitukset:</strong> Sovellus pyytää luvan lähettää ilmoituksia ja toistaa hälytysääniä, jotta
            hälytykset tavoittavat henkilökunnan.
          </li>
        </ul>
        <p>
          <strong>Ilmoitusten sisältö ja asiakastiedot:</strong>
          <br />
          Ilmoitus sisältää vain hälytyksen tyypin ja lukumäärän (esim. ”Tilaus maksettu”). Ilmoituksissa ei koskaan
          välitetä asiakkaan nimeä, lääketietoja tai reseptin sisältöä. Sovellus ei näytä eikä tallenna asiakkaiden
          terveys- tai lääkitystietoja. Sovelluksessa ei käytetä mainontaa tai analytiikkaa, eikä tietoja myydä.
        </p>

        <h2>Henkilötietojen käsittelyn tarkoitus ja peruste</h2>
        <p>
          Tietoja käsitellään, jotta apteekille voidaan toimittaa sen tilaama hälytinpalvelu eli lähettää hälytykset
          oikeaan laitteeseen oikeaan aikaan.
        </p>
        <ul>
          <li>
            Käsittelyn peruste ApoDigi Oy:n ja apteekin välillä on <strong>sopimus</strong> hälytinpalvelun
            toimittamisesta.
          </li>
          <li>
            Jos apteekki nimeää laitteen työntekijän nimen mukaan, nimen käsittelyn perusteena on apteekin{' '}
            <strong>oikeutettu etu</strong> tunnistaa ja hallinnoida laitteita <em>Hälytinlaitteet</em>-näkymässä.
          </li>
        </ul>

        <h2>Tietojen luovutukset ja siirrot</h2>
        <ul>
          <li>
            <strong>Push-ilmoituspalvelut:</strong> Ilmoitusten välittämiseen käytetään Google Firebase Cloud Messaging
            -palvelua ja iOS-laitteilla Applen push-ilmoituspalvelua (APNs). Näille palveluille välitetään laitteen
            ilmoitustunniste ja ilmoituksen tyyppi/lukumäärä. Tietoja voidaan siirtää EU/ETA-alueen ulkopuolelle
            palveluntarjoajien standardisopimuslausekkeiden (SCC) tai EU-US Data Privacy Framework -järjestelyn nojalla.
          </li>
          <li>
            <strong>Apteekin henkilökunta:</strong> Laitteen tiedot näkyvät sen apteekin henkilökunnalle, jolla on
            oikeus hallita hälytinlaitteita. ApoDigi Oy:n henkilöstö käsittelee tietoja vain teknisen ylläpidon ja tuen
            edellyttämässä laajuudessa.
          </li>
          <li>
            Tietoja ei luovuteta markkinointitarkoituksiin. Viranomaisille tietoja luovutetaan vain lakisääteisen
            velvoitteen nojalla.
          </li>
        </ul>

        <h2>Säilytysaika</h2>
        <p>Laitteen tietoja säilytetään palvelun aktiivisen käytön ajan.</p>
        <p>
          Kun apteekki poistaa laitteen käytöstä tai puhelin palautetaan paritustilaan, laitteen nimi poistetaan
          palvelimelta välittömästi. Sen jälkeen jäljelle jääviä teknisiä tietoja (kuten laitteen tunniste ja asetukset)
          voidaan säilyttää palvelun teknistä ylläpitoa ja virhetilanteiden selvittämistä varten. Nämä tiedot eivät ole
          yhdistettävissä yksittäiseen henkilöön.
        </p>

        <h2>Rekisteröidyn oikeudet</h2>
        <p>
          Jos laitteen nimeen on sisällytetty henkilön nimi, kyseisellä henkilöllä on tietosuojalainsäädännön mukainen
          oikeus:
        </p>
        <ul>
          <li>Pyytää pääsyä itseään koskeviin tietoihin</li>
          <li>Pyytää virheellisen tiedon oikaisua tai tiedon poistamista</li>
        </ul>
        <p>
          Pyynnöt tulee osoittaa <strong>ensisijaisesti omalle apteekille</strong>, joka toimii rekisterinpitäjänä ja
          hallitsee laiteasetuksia. Tarvittaessa kyselyt voi lähettää myös osoitteeseen: tietosuojavastaava@treet.fi.
        </p>
        <p>
          Rekisteröidyllä on myös oikeus tehdä valitus tietosuojavaltuutetulle (tietosuoja.fi), mikäli hän katsoo
          henkilötietojensa käsittelyn rikkovan tietosuojalainsäädäntöä.
        </p>

        <h2>Muutokset selosteeseen</h2>
        <p>
          Tätä selostetta päivitetään, jos tietojen käsittelykäytännöt muuttuvat. Voimassa oleva versio on tällä
          sivulla.
        </p>
      </Typography>
    </Container>
  );
};

export default ApteekkihalytinTietosuoja;
